/** @jsxImportSource @emotion/react */
"use client";

import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { Fragment } from "react";

import {
  constantSemanticColors,
  fontWeights,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { BlockProps } from "../../../refactor/Block/Block.emotion";
import { Block } from "../../../refactor/Block/Block.emotion";
import { calculateWon } from "./calculateWon";
import type { TheWordMediumProps } from "./TheWordMedium.emotion";
import { TheWordMedium } from "./TheWordMedium.emotion";
import { TheWordScore } from "./TheWordScore.emotion";

const actionsStyle: Interpolation = {
  marginTop: spacing.m,
};
const blockStyle: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
};
const descriptionStyle: Interpolation = {
  paddingTop: spacing.s,
};
const imgContainerStyle: FunctionInterpolation<{
  color: TheWordMediumProps["color"];
}> = (props) => ({
  alignItems: "center",
  backgroundColor: props.color,
  borderBottomRightRadius: radius.l,
  borderTopRightRadius: radius.l,
  display: "flex",
  justifyContent: "center",
});
const theWordMediumStyle: Interpolation = {
  display: "block",
  [cssMediaQueryMinWidthTablet]: {
    display: "none",
  },
};
const successNumberStyle: FunctionInterpolation<{
  color: TheWordMediumProps["color"];
}> = (props) => ({
  background: constantSemanticColors.onColorHighest,
  borderRadius: radius.full,
  color: props.color,
  display: "inline-flex",
  fontWeight: fontWeights.buttonM,
  height: spacing.l,
  placeContent: "center",
  placeItems: "center",
  width: spacing.l,
});

export type TheWordLargeProps = Pick<
  TheWordMediumProps,
  | "activeState"
  | "centerActions"
  | "color"
  | "description"
  | "focusState"
  | "hoverState"
  | "imgSrc"
  | "summary"
  | "tags"
  | "title"
> &
  Pick<BlockProps, "actions" | "descriptionProps">;

export const TheWordLarge = ({
  actions,
  activeState = false,
  centerActions,
  color = "black",
  description,
  descriptionProps,
  focusState = true,
  hoverState = true,
  imgSrc,
  summary,
  tags,
  title,
}: TheWordLargeProps) => {
  const t = useTranslations();

  const won = calculateWon({ matrix: summary?.matrix });

  return (
    <Fragment>
      <Block
        actions={actions}
        actionsCSS={actionsStyle}
        activeState={activeState}
        color={color}
        description={
          (description ?? won)
            ? t.rich("the_word_successful_explanation", {
                "0": summary.matrix.length,
                b: (chunks) => (
                  <span css={successNumberStyle({ color })}>{chunks}</span>
                ),
              })
            : summary
              ? t("the_word_failed_description")
              : t("the_word_card_description")
        }
        descriptionCSS={descriptionStyle}
        descriptionProps={descriptionProps}
        focusState={focusState}
        hoverState={hoverState}
        imgContainerCSS={imgContainerStyle({ color })}
        imgContainerProps={{
          children: summary ? (
            <TheWordScore color={color} size="l" summary={summary} />
          ) : null,
        }}
        imgSrc={summary ? null : imgSrc}
        css={blockStyle}
        tags={tags}
        title={
          (title ?? summary)
            ? t("the_word_finished_title")
            : t("the_word_title")
        }
      />
      <TheWordMedium
        activeState={activeState}
        centerActions={centerActions}
        color={color}
        description={description}
        focusState={focusState}
        hoverState={hoverState}
        imgSrc={imgSrc}
        summary={summary}
        css={theWordMediumStyle}
        tags={tags}
        title={title}
        won={won}
      />
    </Fragment>
  );
};
